import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaTachometerAlt, FaEdit, FaInbox, FaCog, FaSignOutAlt,
  FaEnvelope, FaConciergeBell, FaStar,
  FaTrash, FaCheck, FaImage, FaVideo, FaCloudUploadAlt, FaPalette,
  FaSync,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useContent } from '../../contexts/ContentContext';
import { toast } from 'react-toastify';
import LangSwitcher from '../../components/LangSwitcher';
import './Admin.css';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { content, saveContent, messages, fetchMessages, deleteMessage, markAsRead, uploadFile } = useContent();
  const [tab, setTab] = useState('overview');
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, video: false, poster: false });
  const [migrating, setMigrating] = useState(false);
  const [migrationLog, setMigrationLog] = useState([]);

  const dateLocale = (i18n.language || 'sk').startsWith('tr')
    ? 'tr-TR' : (i18n.language || 'sk').startsWith('en') ? 'en-US' : 'sk-SK';

  useEffect(() => { setDraft(content); }, [content]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMessages(); }, []);

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  const updateField = (section, field, value) => {
    setDraft({ ...draft, [section]: { ...draft[section], [field]: value } });
  };

  const updateService = (idx, field, value) => {
    const services = [...draft.services];
    services[idx] = { ...services[idx], [field]: value };
    setDraft({ ...draft, services });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveContent(draft);
      toast.success(t('admin.saved'));
    } catch (e) {
      toast.error(t('admin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const migrateAssetsToFirebase = async () => {
    if (!window.confirm(t('admin.migrationConfirm'))) return;

    const ASSETS = [
      { localPath: '/logo.png',                       key: 'branding.logoUrl',   name: 'Logo' },
      { localPath: '/hero-video.mp4',                 key: 'hero.videoUrl',      name: 'Hero video' },
      { localPath: '/images/pexels-mizunokozuki.jpg', key: 'about.images.0',     name: 'About 1' },
      { localPath: '/images/pexels-jack-sparrow.jpg', key: 'about.images.1',     name: 'About 2' },
      { localPath: '/images/about-decor.jpg',         key: 'about.decorImage',   name: 'About decor' },
      { localPath: '/images/ali-yildiz.jpg',          key: 'about.founderImage', name: 'Ali Yildiz' },
      { localPath: '/images/cta-image.jpg',           key: 'about.ctaImage',     name: 'CTA' },
    ];

    setMigrating(true);
    setMigrationLog([]);
    const updated = JSON.parse(JSON.stringify(draft));

    for (const asset of ASSETS) {
      try {
        setMigrationLog((log) => [...log, { name: asset.name, status: 'uploading' }]);
        const res = await fetch(asset.localPath);
        if (!res.ok) throw new Error(`${t('admin.fileNotFound')}: ${asset.localPath}`);
        const blob = await res.blob();
        const fileName = asset.localPath.split('/').pop();
        const file = new File([blob], fileName, { type: blob.type });
        const url = await uploadFile(file, 'site-assets');

        const path = asset.key.split('.');
        let target = updated;
        for (let i = 0; i < path.length - 1; i++) {
          if (path[i] === 'images') continue;
          target[path[i]] = target[path[i]] || {};
          target = target[path[i]];
        }
        const last = path[path.length - 1];
        if (asset.key.includes('images.')) {
          const idx = parseInt(last, 10);
          updated.about.images = [...(updated.about.images || [])];
          updated.about.images[idx] = url;
        } else {
          target[last] = url;
        }

        setMigrationLog((log) => log.map((l) => l.name === asset.name ? { ...l, status: 'done', url } : l));
      } catch (err) {
        setMigrationLog((log) => log.map((l) => l.name === asset.name ? { ...l, status: 'error', error: err.message } : l));
      }
    }

    try {
      await saveContent(updated);
      setDraft(updated);
      toast.success(t('admin.migrationSuccess'));
    } catch (e) {
      toast.error(t('admin.migrationPartial'));
    } finally {
      setMigrating(false);
    }
  };

  const handleUpload = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const url = await uploadFile(file, kind);
      if (kind === 'logo') {
        setDraft((d) => ({ ...d, branding: { ...(d.branding || {}), logoUrl: url } }));
      } else if (kind === 'video') {
        setDraft((d) => ({ ...d, hero: { ...d.hero, videoUrl: url } }));
      } else if (kind === 'poster') {
        setDraft((d) => ({ ...d, hero: { ...d.hero, posterUrl: url } }));
      }
      toast.success(t('admin.uploadSuccess'));
    } catch (err) {
      toast.error(t('admin.uploadError'));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const tabs = [
    { id: 'overview', label: t('admin.dashboard'),    icon: FaTachometerAlt },
    { id: 'branding', label: t('admin.branding.title'), icon: FaPalette },
    { id: 'content',  label: t('admin.content'),     icon: FaEdit },
    { id: 'services', label: t('admin.servicesTab.title'), icon: FaConciergeBell },
    { id: 'messages', label: t('admin.messages'),    icon: FaInbox },
    { id: 'settings', label: t('admin.settings'),    icon: FaCog },
  ];

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="admin">
      <aside className="admin__sidebar">
        <div className="admin__brand">
          <div className="logo-mark" style={{ width: 36, height: 36, fontSize: '1rem' }}>Z</div>
          <span>ZEL Admin</span>
        </div>
        <div className="admin__lang"><LangSwitcher variant="light" /></div>
        <nav className="admin__nav">
          {tabs.map((tt) => (
            <button key={tt.id} className={tab === tt.id ? 'active' : ''} onClick={() => setTab(tt.id)}>
              <tt.icon /> {tt.label}
            </button>
          ))}
        </nav>
        <button className="admin__signout" onClick={onLogout}>
          <FaSignOutAlt /> {t('admin.signOut')}
        </button>
      </aside>

      <main className="admin__main">
        {tab === 'overview' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.dashboard')}</h1>
              <p>{t('admin.overview.subtitle')}</p>
            </div>
            <div className="stats-row">
              <div className="stat-card">
                <div className="stat-card__icon"><FaEnvelope /></div>
                <div className="stat-card__value">{messages.length}</div>
                <div className="stat-card__label">{t('admin.overview.totalMessages')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon"><FaInbox /></div>
                <div className="stat-card__value">{unreadCount}</div>
                <div className="stat-card__label">{t('admin.overview.unread')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon"><FaConciergeBell /></div>
                <div className="stat-card__value">{content.services.length}</div>
                <div className="stat-card__label">{t('admin.overview.services')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon"><FaStar /></div>
                <div className="stat-card__value">{content.testimonials.length}</div>
                <div className="stat-card__label">{t('admin.overview.testimonials')}</div>
              </div>
            </div>

            <div className="admin-card">
              <h3>{t('admin.overview.recentMessages')}</h3>
              {messages.slice(0, 5).map((m) => (
                <div key={m.id} className={`message-row ${!m.read ? 'unread' : ''}`}>
                  <div className="message-row__avatar">{(m.name || 'A').charAt(0).toUpperCase()}</div>
                  <div>
                    <div className="message-row__name">{m.name}</div>
                    <div className="message-row__email">{m.email}</div>
                    <div className="message-row__preview">{(m.message || '').slice(0, 80)}...</div>
                  </div>
                  <div className="message-row__date">{m.createdAt ? new Date(m.createdAt).toLocaleDateString(dateLocale) : ''}</div>
                  <div></div>
                </div>
              ))}
              {messages.length === 0 && <div className="empty-state">{t('admin.overview.noMessages')}</div>}
            </div>
          </>
        )}

        {tab === 'branding' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.branding.title')}</h1>
              <p>{t('admin.branding.subtitle')}</p>
            </div>

            <div className="admin-card" style={{ borderLeft: '4px solid #d4a437' }}>
              <h3><FaSync style={{ marginRight: 8 }} /> {t('admin.branding.migrationTitle')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 18, fontSize: '0.92rem' }}>
                {t('admin.branding.migrationDesc')}
              </p>

              <button
                className="btn btn-dark"
                onClick={migrateAssetsToFirebase}
                disabled={migrating}
                style={{ marginBottom: 16 }}
              >
                <FaCloudUploadAlt style={{ marginRight: 8 }} />
                {migrating ? t('admin.migrating') : t('admin.branding.migrationBtn')}
              </button>

              {migrationLog.length > 0 && (
                <div className="migration-log">
                  {migrationLog.map((item, i) => (
                    <div key={i} className={`mlog mlog--${item.status}`}>
                      <span className="mlog__status">
                        {item.status === 'uploading' && '⏳'}
                        {item.status === 'done' && '✅'}
                        {item.status === 'error' && '❌'}
                      </span>
                      <span className="mlog__name">{item.name}</span>
                      {item.error && <span className="mlog__error"> — {item.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="admin-card">
              <h3><FaImage style={{ marginRight: 8 }} /> {t('admin.branding.logo')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 18, fontSize: '0.92rem' }}>
                {t('admin.branding.logoDesc')}
              </p>

              {draft.branding?.logoUrl && (
                <div className="upload-preview">
                  <img src={draft.branding.logoUrl} alt="Logo" style={{ maxHeight: 80, maxWidth: 240 }} />
                </div>
              )}

              <label className="upload-btn">
                <FaCloudUploadAlt />
                <span>{uploading.logo ? t('admin.uploading') : t('admin.branding.uploadLogo')}</span>
                <input type="file" accept="image/*" hidden onChange={(e) => handleUpload(e, 'logo')} disabled={uploading.logo} />
              </label>

              <div className="admin-grid" style={{ marginTop: 24 }}>
                <div className="admin-field">
                  <label>{t('admin.branding.logoUrlAlt')}</label>
                  <input
                    value={draft.branding?.logoUrl || ''}
                    onChange={(e) => setDraft({ ...draft, branding: { ...(draft.branding || {}), logoUrl: e.target.value } })}
                    placeholder="https://..."
                  />
                </div>
                <div className="admin-field">
                  <label>{t('admin.branding.logoTextAlt')}</label>
                  <input
                    value={draft.branding?.logoText || ''}
                    onChange={(e) => setDraft({ ...draft, branding: { ...(draft.branding || {}), logoText: e.target.value } })}
                    placeholder="ZEL S.R.O"
                  />
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3><FaVideo style={{ marginRight: 8 }} /> {t('admin.branding.video')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 18, fontSize: '0.92rem' }}>
                {t('admin.branding.videoDesc')}
              </p>

              {draft.hero?.videoUrl && (
                <div className="upload-preview">
                  <video src={draft.hero.videoUrl} muted controls style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8 }} />
                </div>
              )}

              <label className="upload-btn">
                <FaCloudUploadAlt />
                <span>{uploading.video ? t('admin.uploading') : t('admin.branding.uploadVideo')}</span>
                <input type="file" accept="video/*" hidden onChange={(e) => handleUpload(e, 'video')} disabled={uploading.video} />
              </label>

              <div className="admin-field" style={{ marginTop: 20 }}>
                <label>{t('admin.branding.videoUrlAlt')}</label>
                <input
                  value={draft.hero?.videoUrl || ''}
                  onChange={(e) => updateField('hero', 'videoUrl', e.target.value)}
                  placeholder="https://...mp4"
                />
              </div>
            </div>

            <div className="admin-card">
              <h3><FaImage style={{ marginRight: 8 }} /> {t('admin.branding.poster')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 18, fontSize: '0.92rem' }}>
                {t('admin.branding.posterDesc')}
              </p>

              {draft.hero?.posterUrl && (
                <div className="upload-preview">
                  <img src={draft.hero.posterUrl} alt="Poster" style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 8 }} />
                </div>
              )}

              <label className="upload-btn">
                <FaCloudUploadAlt />
                <span>{uploading.poster ? t('admin.uploading') : t('admin.branding.uploadPoster')}</span>
                <input type="file" accept="image/*" hidden onChange={(e) => handleUpload(e, 'poster')} disabled={uploading.poster} />
              </label>

              <div className="admin-field" style={{ marginTop: 20 }}>
                <label>{t('admin.branding.posterUrlAlt')}</label>
                <input
                  value={draft.hero?.posterUrl || ''}
                  onChange={(e) => updateField('hero', 'posterUrl', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <button className="btn btn-dark" onClick={save} disabled={saving}>
              {saving ? t('admin.saving') : t('admin.save')}
            </button>
          </>
        )}

        {tab === 'content' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.content')}</h1>
              <p>{t('admin.contentTab.subtitle')}</p>
            </div>

            <div className="admin-card">
              <h3>{t('admin.contentTab.heroSection')}</h3>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.titleSk')}</label>
                  <input value={draft.hero?.titleSk || ''} onChange={(e) => updateField('hero', 'titleSk', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.titleEn')}</label>
                  <input value={draft.hero?.titleEn || ''} onChange={(e) => updateField('hero', 'titleEn', e.target.value)} />
                </div>
              </div>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.descSk')}</label>
                  <textarea rows="3" value={draft.hero?.subtitleSk || ''} onChange={(e) => updateField('hero', 'subtitleSk', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.descEn')}</label>
                  <textarea rows="3" value={draft.hero?.subtitleEn || ''} onChange={(e) => updateField('hero', 'subtitleEn', e.target.value)} />
                </div>
              </div>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.videoUrl')}</label>
                  <input value={draft.hero?.videoUrl || ''} onChange={(e) => updateField('hero', 'videoUrl', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.posterUrl')}</label>
                  <input value={draft.hero?.posterUrl || ''} onChange={(e) => updateField('hero', 'posterUrl', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3>{t('admin.contentTab.aboutSection')}</h3>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.titleSk')}</label>
                  <input value={draft.about?.titleSk || ''} onChange={(e) => updateField('about', 'titleSk', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.titleEn')}</label>
                  <input value={draft.about?.titleEn || ''} onChange={(e) => updateField('about', 'titleEn', e.target.value)} />
                </div>
              </div>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.bodySk')}</label>
                  <textarea rows="5" value={draft.about?.bodySk || ''} onChange={(e) => updateField('about', 'bodySk', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.bodyEn')}</label>
                  <textarea rows="5" value={draft.about?.bodyEn || ''} onChange={(e) => updateField('about', 'bodyEn', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3>{t('admin.contentTab.contactSection')}</h3>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.phone')}</label>
                  <input value={draft.contact?.phone || ''} onChange={(e) => updateField('contact', 'phone', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.email')}</label>
                  <input value={draft.contact?.email || ''} onChange={(e) => updateField('contact', 'email', e.target.value)} />
                </div>
              </div>
              <div className="admin-grid">
                <div className="admin-field">
                  <label>{t('admin.contentTab.addressSk')}</label>
                  <input value={draft.contact?.addressSk || ''} onChange={(e) => updateField('contact', 'addressSk', e.target.value)} />
                </div>
                <div className="admin-field">
                  <label>{t('admin.contentTab.addressEn')}</label>
                  <input value={draft.contact?.addressEn || ''} onChange={(e) => updateField('contact', 'addressEn', e.target.value)} />
                </div>
              </div>
            </div>

            <button className="btn btn-dark" onClick={save} disabled={saving}>
              {saving ? t('admin.saving') : t('admin.save')}
            </button>
          </>
        )}

        {tab === 'services' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.servicesTab.title')}</h1>
              <p>{t('admin.servicesTab.subtitle')}</p>
            </div>
            <div className="admin-card">
              <h3>{t('admin.servicesTab.list')}</h3>
              {draft.services?.map((s, i) => (
                <div key={s.id} className="service-edit-row">
                  <div className="admin-field" style={{ margin: 0 }}>
                    <label>{t('admin.servicesTab.icon')}</label>
                    <input value={s.icon} onChange={(e) => updateService(i, 'icon', e.target.value)} />
                  </div>
                  <div className="admin-field" style={{ margin: 0 }}>
                    <label>{t('admin.servicesTab.titleSk')}</label>
                    <input value={s.titleSk} onChange={(e) => updateService(i, 'titleSk', e.target.value)} />
                  </div>
                  <div className="admin-field" style={{ margin: 0 }}>
                    <label>{t('admin.servicesTab.titleEn')}</label>
                    <input value={s.titleEn} onChange={(e) => updateService(i, 'titleEn', e.target.value)} />
                  </div>
                  <div className="admin-field" style={{ margin: 0 }}>
                    <label>{t('admin.servicesTab.descSk')}</label>
                    <input value={s.descSk} onChange={(e) => updateService(i, 'descSk', e.target.value)} />
                  </div>
                  <div className="admin-field" style={{ margin: 0 }}>
                    <label>{t('admin.servicesTab.descEn')}</label>
                    <input value={s.descEn} onChange={(e) => updateService(i, 'descEn', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btn-dark" onClick={save} disabled={saving}>
              {saving ? t('admin.saving') : t('admin.save')}
            </button>
          </>
        )}

        {tab === 'messages' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.messages')}</h1>
              <p>{t('admin.messagesTab.subtitle')}</p>
            </div>
            <div className="admin-card">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <FaInbox style={{ fontSize: '3rem', opacity: 0.3, marginBottom: 12 }} />
                  <p>{t('admin.messagesTab.noMessages')}</p>
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`message-row ${!m.read ? 'unread' : ''}`}>
                    <div className="message-row__avatar">{(m.name || 'A').charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="message-row__name">{m.name} {m.subject && <span style={{fontWeight: 400, color: 'var(--color-text-muted)'}}>· {m.subject}</span>}</div>
                      <div className="message-row__email">{m.email} {m.phone && <>· {m.phone}</>}</div>
                      <div className="message-row__preview">{m.message}</div>
                    </div>
                    <div className="message-row__date">{m.createdAt ? new Date(m.createdAt).toLocaleString(dateLocale) : ''}</div>
                    <div className="message-row__actions">
                      {!m.read && (
                        <button className="icon-btn" onClick={() => markAsRead(m.id)} title={t('admin.messagesTab.markRead')}>
                          <FaCheck />
                        </button>
                      )}
                      <button className="icon-btn danger" onClick={() => deleteMessage(m.id)} title={t('admin.messagesTab.deleteMsg')}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === 'settings' && (
          <>
            <div className="admin__header">
              <h1>{t('admin.settings')}</h1>
              <p>{t('admin.settingsTab.subtitle')}</p>
            </div>
            <div className="admin-card">
              <h3>{t('admin.settingsTab.firebase')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>
                {t('admin.settingsTab.firebaseDesc')}
              </p>
              <div style={{ background: '#fbfaf6', padding: 16, borderRadius: 8, fontFamily: 'monospace', fontSize: 13 }}>
                REACT_APP_FIREBASE_API_KEY=...<br />
                REACT_APP_FIREBASE_AUTH_DOMAIN=...<br />
                REACT_APP_FIREBASE_PROJECT_ID=...<br />
                REACT_APP_FIREBASE_STORAGE_BUCKET=...<br />
                REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...<br />
                REACT_APP_FIREBASE_APP_ID=...
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
